/**
 * Scene manager — CRUD over saved scenes plus JSON import/export.
 *
 * Scenes live in localStorage (see `studioStore`); the JSON file is the portable
 * form, so a promo setup can be shared or version-controlled by hand.
 */
import { useRef, useState } from 'react'
import { Copy, Download, FileStack, Pencil, Plus, Trash2, Upload, X } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useFlythroughStore } from '@/features/flythrough'
import { useStudioStore, selectActiveScene } from '../store/studioStore'
import { compactScene, parseSceneFile, serializeScene } from '../schema'
import { FIELD, LABEL, PANEL, SECTION_TITLE } from './studioStyles'
import type { CinematicSceneSettings } from '../types'

interface Props {
  locationId: string | null
  locationSlug: string | null
  origin: [number, number] | null
  /** Flythrough scope key for the active scene, so tours travel with it. */
  flythroughScope: string
  onClose: () => void
}

export function SceneManagerPanel({
  locationId,
  locationSlug,
  origin,
  flythroughScope,
  onClose,
}: Props) {
  const scenes = useStudioStore((s) => s.scenes)
  const activeSceneId = useStudioStore((s) => s.activeSceneId)
  const scene = useStudioStore(selectActiveScene)
  const createScene = useStudioStore((s) => s.createScene)
  const importScene = useStudioStore((s) => s.importScene)
  const renameScene = useStudioStore((s) => s.renameScene)
  const duplicateScene = useStudioStore((s) => s.duplicateScene)
  const deleteScene = useStudioStore((s) => s.deleteScene)
  const setActiveScene = useStudioStore((s) => s.setActiveScene)
  const updateSettings = useStudioStore((s) => s.updateSettings)

  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  const sceneList = Object.values(scenes)

  const handleExport = () => {
    if (!scene) return
    // Fold the live camera tours back into the scene document so the export is
    // complete — the flythrough module owns them at runtime.
    const playlists = useFlythroughStore.getState().getScope(flythroughScope).playlists
    const doc = serializeScene(compactScene({ ...scene, playlists }))
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${scene.meta.name.replace(/[^a-z0-9-_]+/gi, '-').toLowerCase() || 'scene'}.json`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  const handleImport = async (file: File) => {
    try {
      const result = parseSceneFile(JSON.parse(await file.text()))
      if (!result.ok || !result.scene) {
        toast.error(`Could not import: ${result.error}`)
        return
      }
      if (result.warning) toast.warning(result.warning)

      const imported = result.scene
      // Warn (don't block) when the scene came from another airfield: positions
      // are origin-relative, so the layout is preserved but sits over different
      // pavement.
      if (imported.meta.locationId && locationId && imported.meta.locationId !== locationId) {
        toast.warning(
          `Scene was authored at ${imported.meta.locationSlug ?? 'another location'} — ` +
            `aircraft are placed relative to this location's centre.`
        )
      }
      const id = importScene(imported)
      // Hand the camera tours to the flythrough module under the new scene's scope.
      useFlythroughStore.getState().loadScope(`cinematic:${id}`, imported.playlists)
      toast.success(`Imported "${imported.meta.name}".`)
    } catch {
      toast.error('Could not import: the file is not valid JSON.')
    }
  }

  const patchSettings = (patch: Partial<CinematicSceneSettings>) => updateSettings(patch)

  return (
    <div className={cn(PANEL, 'flex max-h-[70vh] w-72 flex-col')}>
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <FileStack className="h-4 w-4 text-sky-300" />
        <span className={SECTION_TITLE}>Scenes</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"
          title="Import scene JSON"
        >
          <Upload className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleExport}
          disabled={!scene}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white disabled:opacity-30"
          title="Export scene JSON"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"
          title="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void handleImport(f)
          e.target.value = ''
        }}
      />

      {/* Scene selector */}
      <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-2">
        {renaming && scene ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              const n = draft.trim()
              if (n) renameScene(scene.meta.id, n)
              setRenaming(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') setRenaming(false)
            }}
            className={cn(FIELD, 'flex-1')}
          />
        ) : (
          <select
            value={activeSceneId ?? ''}
            onChange={(e) => setActiveScene(e.target.value || null)}
            className={cn(FIELD, 'flex-1')}
          >
            {sceneList.length === 0 && <option value="">No scenes yet</option>}
            {sceneList.map((s) => (
              <option key={s.meta.id} value={s.meta.id} className="bg-slate-900">
                {s.meta.name}
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          disabled={!scene}
          onClick={() => {
            if (!scene) return
            setDraft(scene.meta.name)
            setRenaming(true)
          }}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white disabled:opacity-30"
          title="Rename scene"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() =>
            createScene(`Scene ${sceneList.length + 1}`, locationId, locationSlug, origin)
          }
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"
          title="New scene"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={!scene}
          onClick={() => scene && duplicateScene(scene.meta.id)}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white disabled:opacity-30"
          title="Duplicate scene"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={!scene}
          onClick={() => scene && deleteScene(scene.meta.id)}
          className="rounded p-1 text-white/40 hover:bg-rose-500/20 hover:text-rose-200 disabled:opacity-30"
          title="Delete scene"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Look + timing */}
      {scene && (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
          <span className={SECTION_TITLE}>Look</span>

          <label className="block">
            <span className={LABEL}>Time of day</span>
            <select
              value={scene.settings.lightPreset}
              onChange={(e) =>
                patchSettings({ lightPreset: e.target.value as CinematicSceneSettings['lightPreset'] })
              }
              className={FIELD}
            >
              <option value="dawn" className="bg-slate-900">Dawn</option>
              <option value="day" className="bg-slate-900">Day</option>
              <option value="dusk" className="bg-slate-900">Dusk</option>
              <option value="night" className="bg-slate-900">Night</option>
            </select>
          </label>

          <label className="flex items-center gap-2 text-[11px] text-white/70">
            <input
              type="checkbox"
              checked={scene.settings.showRoofs}
              onChange={(e) => patchSettings({ showRoofs: e.target.checked })}
              className="h-3 w-3 accent-sky-400"
            />
            Show hangar roofs
          </label>

          <span className={cn(SECTION_TITLE, 'block pt-1')}>Timing</span>

          <label className="block">
            <span className={LABEL}>Scene length (s)</span>
            <input
              type="number"
              min={1}
              step={5}
              value={Math.round(scene.settings.durationMs / 1000)}
              onChange={(e) =>
                patchSettings({ durationMs: Math.max(1000, Number(e.target.value) * 1000) })
              }
              className={cn(FIELD, 'tabular-nums')}
            />
          </label>

          <label className="flex items-center gap-2 text-[11px] text-white/70">
            <input
              type="checkbox"
              checked={scene.settings.loopScene}
              onChange={(e) => patchSettings({ loopScene: e.target.checked })}
              className="h-3 w-3 accent-sky-400"
            />
            Loop the scene
          </label>

          <p className="pt-1 text-[10px] leading-relaxed text-white/30">
            Scenes are saved in this browser. Export to JSON to keep or share one.
          </p>
        </div>
      )}
    </div>
  )
}
